import { isAuPhone, isWithAgent, pushMarkerIso, threadLastTimestamp } from '@atink/domain';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { useUIStore } from '@/state/uiStore';
import { eventBus } from '@/events/eventBus';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { pushToAgentIntent } from '@/api/pushIntents';
import { intentFailureCopy, type IntentFailure } from '@/api/intents';
import { withServerAuthoredWrites } from '@/state/persistenceBridge';
import type { SequenceState } from '@/domain/sequenceFlow';

export interface PushLeadInput {
  customerId: string;
  phone: string;
  firstName?: string;
  lastName?: string;
  /** The rule-built summary from the thread, as the Messenger edited it. */
  summary?: string;
  pinnedCallbackAt?: string;
}

export type PushLeadResult = { ok: true } | { ok: false; reason: 'phone-required' | 'already-pushed' | 'no-customer' };

/**
 * The Messenger hands a lead to the Agent (B8 / 14ym9bv75mx + 75nf, diagram 03).
 * A phone number is required (no number, no push: they stay a warm lead in the
 * inbox). The typed name and the phone are saved on the customer, and the
 * sequence record is marked pushed with the summary and the optional callback.
 *
 * Served build: ONE server transaction (POST /api/intents/push-to-agent) decides;
 * the store mirrors it without bridge PUTs and restores it on a refusal. Store-
 * fallback (no server): applied locally.
 */
export function pushLeadToAgent(input: PushLeadInput): PushLeadResult {
  const brain = useBrainStore.getState();
  const customer = brain.customers[input.customerId];
  if (!customer) return { ok: false, reason: 'no-customer' };
  const phone = input.phone.trim();
  if (!isAuPhone(phone)) return { ok: false, reason: 'phone-required' };
  const prevSeq = brain.sequenceState[input.customerId];
  // Anas 8 Oct: once pushed, the lead is the Agent's for good (red or booked
  // included); the shared rule, as the server's push intent applies it.
  if (isWithAgent(customer, prevSeq)) return { ok: false, reason: 'already-pushed' };

  const first = input.firstName?.trim() ?? '';
  const last = input.lastName?.trim() ?? '';
  const summary = input.summary?.trim() || undefined;
  const nowIso = useSimulationStore.getState().clockIso;
  const threadLast = threadLastTimestamp(customer.conversation);
  const nextCustomer = {
    ...customer,
    phone,
    ...(first ? { firstName: first, lastName: last } : {}),
    // tv-3zjo: the durable "pushed to the Agent" marker, first push time kept.
    // Served, it mirrors the server's stamp; store-fallback, it is the only one.
    // tv-9nae: stamped after everything already in the thread.
    pushedToAgentAt: customer.pushedToAgentAt ?? pushMarkerIso(nowIso, threadLast),
  };
  const nextSeq: SequenceState = {
    ...(prevSeq ?? {}),
    stage: prevSeq && prevSeq.stage !== 'red' ? prevSeq.stage : 1,
    cohort: 'fresh',
    sourceConversationId: input.customerId,
    redKind: undefined,
    nextDueAt: undefined,
    description: summary,
    pinnedCallbackAt: input.pinnedCallbackAt,
    pinnedAt: input.pinnedCallbackAt ? useSimulationStore.getState().clockIso : undefined,
  };
  const apply = () => {
    const b = useBrainStore.getState();
    b.upsertCustomer(nextCustomer);
    b.setSequenceState(input.customerId, nextSeq);
  };

  if (isStoreFallbackEnabled()) {
    apply();
    brain.appendActivity({
      id: `act-push-${input.customerId}-${new Date(nowIso).getTime()}`,
      type: 'pushed-to-queue',
      customerId: input.customerId,
      body: `${first || customer.firstName} ${first ? last : customer.lastName} pushed to the booking queue.${summary ? ` ${summary}` : ''}`,
      timestamp: nowIso,
    });
    eventBus.publish('sequence:pushed', { customerId: input.customerId });
    return { ok: true };
  }

  const pairs = [
    ['customers', input.customerId],
    ['sequenceState', input.customerId],
  ] as const;
  withServerAuthoredWrites(pairs, apply);
  eventBus.publish('sequence:pushed', { customerId: input.customerId });
  const revert = (failure: IntentFailure) => {
    withServerAuthoredWrites(pairs, () => {
      useBrainStore.setState((s) => {
        const sequenceState = { ...s.sequenceState };
        if (prevSeq) sequenceState[input.customerId] = prevSeq;
        else delete sequenceState[input.customerId];
        return { customers: { ...s.customers, [input.customerId]: customer }, sequenceState };
      });
    });
    const retry = intentFailureCopy(failure).retry;
    useUIStore.getState().pushNotification({
      intensity: 'critical',
      title: "Couldn't push the lead to the Agent",
      body: retry
        ? "We couldn't reach the server, so nothing changed. Try again."
        : `The server refused it: ${failure.message ?? failure.code ?? failure.error}. Nothing changed.`,
      ttlMs: 10000,
    });
  };
  void pushToAgentIntent({
    customerId: input.customerId,
    phone,
    ...(first ? { firstName: first, lastName: last } : {}),
    ...(summary ? { summary } : {}),
    ...(input.pinnedCallbackAt ? { pinnedCallbackAt: input.pinnedCallbackAt } : {}),
    ...(threadLast ? { threadLastAt: threadLast } : {}),
    nowIso,
  })
    .then((res) => {
      if (!res.ok) return revert(res);
      // alreadyResolved: another seat pushed it first. The server's record stands
      // and reaches this tab through the sequenceState doorbell; nothing to undo.
    })
    .catch(() => revert({ ok: false, kind: 'transient', status: 0, error: 'network' }));
  return { ok: true };
}
